import React, { useState, useEffect } from 'react';
import apiClient from '../api/client';
import { Plus, CheckCircle, Edit2 } from 'lucide-react';
import { format } from 'date-fns';

const TaskManagement = () => {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assignee_id: '',
    due_date: '',
    priority: 'medium',
    category: 'general'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tasksRes, usersRes] = await Promise.all([
        apiClient.get('/tasks'),
        apiClient.get('/users')
      ]);
      setTasks(tasksRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/tasks', formData);
      alert('Task created!');
      setShowForm(false);
      setFormData({ title: '', description: '', assignee_id: '', due_date: '', priority: 'medium', category: 'general' });
      fetchData();
    } catch (error) {
      alert('Failed to create task');
    }
  };

  const handleComplete = async (taskId) => {
    try {
      await apiClient.put(`/tasks/${taskId}`, {
        status: 'completed',
        completion_date: new Date().toISOString()
      });
      alert('Task completed!');
      fetchData();
    } catch (error) {
      alert('Failed');
    }
  };

  const handleGrade = async (taskId) => {
    const grade = prompt('Grade (0-100):');
    if (grade) {
      try {
        await apiClient.put(`/tasks/${taskId}`, {
          completion_notes: `Grade: ${grade}/100`
        });
        alert('Graded!');
        fetchData();
      } catch (error) {
        alert('Failed');
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Task Management</h1>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg"
        >
          <Plus size={20} className="mr-2" />
          Create Task
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6">
            <h2 className="text-2xl font-bold mb-4">Create Task</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Task title"
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description"
                className="w-full px-3 py-2 border rounded-lg"
                rows="3"
                required
              />
              <div className="grid grid-cols-2 gap-4">
                <select
                  value={formData.assignee_id}
                  onChange={(e) => setFormData({ ...formData, assignee_id: e.target.value })}
                  className="px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="">Select user...</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>{user.name}</option>
                  ))}
                </select>
                <input
                  type="date"
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value + 'T00:00:00Z' })}
                  className="px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="px-3 py-2 border rounded-lg"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="px-3 py-2 border rounded-lg"
                >
                  <option value="general">General</option>
                  <option value="capa">CAPA</option>
                  <option value="audit">Audit</option>
                  <option value="training">Training</option>
                </select>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border rounded-lg"
                >
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg">
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {tasks.map((task) => (
              <tr key={task.id}>
                <td className="px-6 py-4">
                  <p className="font-medium">{task.title}</p>
                  <p className="text-sm text-gray-500">{task.description.substring(0, 50)}...</p>
                </td>
                <td className="px-6 py-4">{users.find(u => u.id === task.assignee_id)?.name || 'Unknown'}</td>
                <td className="px-6 py-4">
                  <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                    {task.status}
                  </span>
                </td>
                <td className="px-6 py-4">{format(new Date(task.due_date), 'MMM dd')}</td>
                <td className="px-6 py-4">
                  <div className="flex space-x-2">
                    {task.status !== 'completed' && (
                      <button onClick={() => handleComplete(task.id)} className="text-green-600">
                        <CheckCircle size={18} />
                      </button>
                    )}
                    {task.status === 'completed' && (
                      <button onClick={() => handleGrade(task.id)} className="text-blue-600">
                        <Edit2 size={18} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {tasks.length === 0 && <div className="text-center py-8 text-gray-500">No tasks</div>}
      </div>
    </div>
  );
};

export default TaskManagement;